<template>
  <div class="wrapper" v-if="cards.length">
    <div class="info-bar">
      <div class="progress">
        <div class="progress-fill" :style="{ width: progressPct + '%' }"></div>
      </div>
      <div class="stats">
        <span>Card {{ index + 1 }} / {{ cards.length }}</span>
        <span>Score: {{ score }}</span>
        <span>Streak: {{ streak }}</span>
      </div>
    </div>

    <div class="card-outer" :class="{ flipped: showBack }" @click="flip">
      <button class="btn nav prev" @click.stop="prev" :disabled="index === 0">
        <Icon icon="mdi:chevron-left" width="36" height="36" />
      </button>
      <div class="card-inner">
        <div class="card face front">
          <img :src="current.image" :alt="currentAlt" @error="onImgError" />
          <small class="hint">click or press Space/Enter to flip</small>
        </div>
        <div class="card face back">
          <div class="name">{{ current.name }}</div>
          <div class="grading">
            <button class="btn bad" @click.stop="mark(false)">
              <Icon icon="mdi:close" width="32" height="32" />
            </button>
            <button class="btn good" @click.stop="mark(true)">
              <Icon icon="mdi:check" width="32" height="32" />
            </button>
          </div>
        </div>
      </div>
      <button class="btn nav next" @click.stop="next" :disabled="index === cards.length - 1">
        <Icon icon="mdi:chevron-right" width="36" height="36" />
      </button>
    </div>
  </div>

  <div class="empty" v-else>
    <p>No users to study yet.</p>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue';
export default {
  name: 'FlashCard',
  components: { Icon },
  props: {
    users: { type: Array, default: () => [] },
  },
  data() {
    return {
      cards: [],
      index: 0,
      showBack: false,
      score: 0,
      streak: 0,
      seen: new Set(),
      fallbackAvatar:
        'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" fill="#ccc"/><path d="M4 22c0-4 4-6 8-6s8 2 8 6" fill="#ccc"/></svg>',
    };
  },
  computed: {
    current() {
      return this.cards[this.index] || {};
    },
    currentAlt() {
      return this.current?.name ? `Photo of ${this.current.name}` : 'Photo';
    },
    progressPct() {
      if (!this.cards.length) return 0;
      return Math.round(((this.index + 1) / this.cards.length) * 100);
    },
  },
  watch: {
    users: {
      immediate: true,
      handler(list) {
        // normalize & dedupe by id
        const seenIds = new Set();
        this.cards = (list || [])
          .filter(u => u && (u.name || u.display_name))
          .map(u => ({
            id: u.id ?? u.user_id ?? u.name, // fallback
            name: u.name ?? u.display_name,
            image: u.image ?? u.image_url ?? u.img,
          }))
          .filter(u => {
            if (!u.name) return false;
            if (seenIds.has(u.id)) return false;
            seenIds.add(u.id);
            return true;
          });

        this.index = 0;
        this.showBack = false;
        this.seen = new Set();
        this.score = 0;
        this.streak = 0;
      },
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKey);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKey);
  },
  methods: {
    flip() {
      if (!this.cards.length) return;
      this.showBack = !this.showBack;
    },
    next() {
      if (this.index < this.cards.length - 1) {
        this.index++;
        this.showBack = false;
      }
    },
    prev() {
      if (this.index > 0) {
        this.index--;
        this.showBack = false;
      }
    },
    mark(correct) {
      // Only count first time we mark this card
      const key = this.current.id ?? `${this.index}`;
      if (!this.seen.has(key)) {
        this.seen.add(key);
        if (correct) {
          this.score += 1;
          this.streak += 1;
        } else {
          this.streak = 0;
        }
      }
      // Go to next card automatically if possible
      if (this.index < this.cards.length - 1) {
        this.index++;
        this.showBack = false;
      }
    },
    onKey(e) {
      const k = e.key.toLowerCase();
      if (k === ' ' || k === 'enter') {
        e.preventDefault();
        this.flip();
      } else if (k === 'arrowright' || k === 'n') {
        this.next();
      } else if (k === 'arrowleft' || k === 'p') {
        this.prev();
      } else if (k === 'y') {
        if (this.showBack) this.mark(true);
      } else if (k === 'x') {
        if (this.showBack) this.mark(false);
      }
    },
    onImgError(ev) {
      ev.target.src = this.fallbackAvatar;
    },
  },
};
</script>

<style scoped>
.wrapper {
  max-width: 560px;
  margin: 0 auto;
}

.info-bar {
  margin-bottom: 12px;
}

.stats {
  display: flex;
  gap: 12px;
  justify-content: center;
  font-size: 14px;
  color: #555;
  margin-top: 6px;
}

.progress {
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: #eee;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  width: 0%;
  background: #7aa2ff;
  transition: width 200ms ease;
}

.card-outer {
  position: relative;
  perspective: 1000px;
  height: 360px;
  margin: 12px 0 16px;
}


.card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 350ms ease;
  transform-style: preserve-3d;
}

.card-outer.flipped .card-inner {
  transform: rotateY(180deg);
}

.card.face {
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, .08);
  display: grid;
  place-items: center;
  background: #fff;
  padding: 18px;
}

.card.front img {
  width: 220px;
  height: 220px;
  object-fit: cover;
  border-radius: 50%;
  box-shadow: 0 4px 18px rgba(0, 0, 0, .06);
}

.card.back {
  transform: rotateY(180deg);
}

.name {
  font-size: 28px;
  font-weight: 700;
}

.hint {
  margin-top: 10px;
  color: #888;
  font-size: 12px;
}

.controls,
.grading {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.btn {
  border: none;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f2f4f8;
  font-weight: 600;
  cursor: pointer;
}

.btn:hover {
  filter: brightness(0.97);
}

.btn:disabled {
  opacity: .5;
  cursor: not-allowed;
}

.btn.good {
  background: #d7f5e5;
}

.btn.bad {
  background: #ffe1e1;
}

.empty {
  color: #777;
  margin-top: 48px;
}

.btn.nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.btn.nav:hover {
  background: #f2f4f8;
}

.btn.nav:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn.nav.prev {
  left: -50px;
  /* adjust how far outside the card */
}

.btn.nav.next {
  right: -50px;
}
</style>
